import { useState, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  File, 
  X, 
  AlertCircle,
  Loader2,
  FileBox,
  Sparkles,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { formatFileSize } from '@/lib/utils'

interface FileUploadProps {
  onFileSelect: (file: File) => void
  onUpload?: (file: File) => Promise<void>
  accept?: string
  maxSize?: number
  isUploading?: boolean
  uploadProgress?: number
  className?: string
}

const SUPPORTED_FORMATS = ['.stl', '.obj', '.3mf', '.step', '.iges', '.fbx', '.glb']
const DEFAULT_MAX_SIZE = 100 * 1024 * 1024 // 100MB

export default function FileUpload({
  onFileSelect,
  onUpload,
  accept = '.stl,.obj,.3mf,.step,.iges,.fbx,.glb',
  maxSize = DEFAULT_MAX_SIZE,
  isUploading = false,
  uploadProgress = 0,
  className = '',
}: FileUploadProps) {
  const [isDragOver, setIsDragOver] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const validateFile = useCallback((file: File): string | null => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!SUPPORTED_FORMATS.includes(ext)) {
      return `Unsupported format. Please use: ${SUPPORTED_FORMATS.join(', ')}`
    }
    if (file.size > maxSize) {
      return `File too large. Maximum size is ${formatFileSize(maxSize)}`
    }
    return null
  }, [maxSize])

  const handleFile = useCallback((file: File) => {
    const validationError = validateFile(file)
    if (validationError) {
      setError(validationError)
      setSelectedFile(null)
      return
    }
    setError(null)
    setSelectedFile(file)
    onFileSelect(file)
  }, [validateFile, onFileSelect])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    
    const file = e.dataTransfer.files[0]
    if (file) {
      handleFile(file)
    }
  }, [handleFile])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      handleFile(file)
    }
  }, [handleFile])

  const handleRemove = useCallback(() => {
    setSelectedFile(null)
    setError(null)
  }, [])

  const handleUpload = useCallback(async () => {
    if (selectedFile && onUpload) {
      await onUpload(selectedFile)
    }
  }, [selectedFile, onUpload])

  return (
    <div className={className}>
      <AnimatePresence mode="wait">
        {!selectedFile ? (
          <motion.div
            key="dropzone"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className={`
              relative border-2 border-dashed rounded-2xl p-8 transition-all duration-200
              ${isDragOver 
                ? 'border-primary-500 bg-primary-500/10' 
                : 'border-surface-700 hover:border-surface-600 bg-surface-800/50'
              }
            `}
          >
            <input
              type="file"
              accept={accept}
              onChange={handleInputChange}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="flex flex-col items-center text-center">
              <motion.div
                animate={{ 
                  y: isDragOver ? -5 : 0,
                  scale: isDragOver ? 1.1 : 1,
                }}
                className={`
                  w-16 h-16 rounded-2xl flex items-center justify-center mb-4
                  ${isDragOver ? 'bg-primary-500/20' : 'bg-surface-700'}
                `}
              >
                <FileBox className={`w-8 h-8 ${isDragOver ? 'text-primary-400' : 'text-surface-400'}`} />
              </motion.div>
              
              <h3 className="text-lg font-medium text-white mb-2">
                {isDragOver ? 'Drop your file here' : 'Upload 3D Model'}
              </h3>
              <p className="text-surface-400 text-sm mb-4">
                Drag and drop or click to browse
              </p>
              
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                {SUPPORTED_FORMATS.map(format => (
                  <span 
                    key={format}
                    className="px-2 py-1 bg-surface-700 rounded text-xs text-surface-300 font-mono"
                  >
                    {format}
                  </span>
                ))}
              </div>
              
              <p className="text-surface-500 text-xs">
                Maximum file size: {formatFileSize(maxSize)}
              </p>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-4 left-4 right-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2"
              >
                <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                <span className="text-sm text-red-400">{error}</span>
              </motion.div>
            )}
          </motion.div>
        ) : (
          <motion.div
            key="file-info"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-surface-800 border border-surface-700 rounded-2xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                <File className="w-7 h-7 text-primary-400" />
              </div>
              
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-white truncate">{selectedFile.name}</h4>
                <p className="text-sm text-surface-400 mt-0.5">
                  {formatFileSize(selectedFile.size)}
                </p>
                
                {isUploading && (
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-surface-400">Uploading...</span>
                      <span className="text-primary-400">{uploadProgress}%</span>
                    </div>
                    <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-primary-500 to-ai-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
              
              {!isUploading && (
                <button
                  onClick={handleRemove}
                  className="p-2 rounded-lg hover:bg-surface-700 text-surface-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {!isUploading && onUpload && (
              <div className="flex gap-3 mt-6">
                <Button 
                  variant="ghost" 
                  className="flex-1"
                  onClick={handleRemove}
                >
                  Change File
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleUpload}
                >
                  <Sparkles className="w-4 h-4" />
                  Analyze & Upload
                </Button>
              </div>
            )}

            {isUploading && (
              <div className="flex items-center justify-center gap-2 mt-6 text-surface-400">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">Processing with AI...</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
